import React, { useEffect, useRef, useState } from 'react';
import { supervisorLogAPI, employeeAttachmentsAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Plus, ThumbsUp, AlertTriangle, ClipboardList as ClipboardIcon, Bell, Trash2, Pencil, Paperclip, Upload, Download, FileText, Printer,
} from 'lucide-react';
import { cn } from '../lib/utils';

// Display labels — letters (A–G) are internal codes only and never shown.
const CATEGORIES = [
  { value: 'A', label: 'Professionalism' },
  { value: 'B', label: 'Safety & Security' },
  { value: 'C', label: 'Customer Service' },
  { value: 'D', label: 'Uniform & Appearance' },
  { value: 'E', label: 'Understanding of Requirements' },
  { value: 'F', label: 'Core Values' },
  { value: 'G', label: 'Attendance' },
];
const CATEGORY_LABEL = Object.fromEntries(CATEGORIES.map(c => [c.value, c.label]));

const ENTRY_TYPES = [
  {
    value: 'deficiency',
    label: 'Deficiency',
    Icon: AlertTriangle,
    border: 'border-red-500',
    badge: 'bg-red-100 text-red-800',
  },
  {
    value: 'praise',
    label: 'Positive Observation',
    Icon: ThumbsUp,
    border: 'border-emerald-500',
    badge: 'bg-emerald-100 text-emerald-800',
  },
  {
    value: 'log_entry',
    label: 'Log Entry',
    Icon: ClipboardIcon,
    border: 'border-blue-500',
    badge: 'bg-blue-100 text-blue-800',
  },
];
const TYPE_BY_VALUE = Object.fromEntries(ENTRY_TYPES.map(t => [t.value, t]));

const todayStr = () => new Date().toISOString().slice(0, 10);

/** Empty form state for create / edit. */
const blankForm = () => ({
  entry_type: 'log_entry',
  categories: [],
  incident_date: todayStr(),
  description: '',
  corrective_action: '',
  follow_up_required: false,
  follow_up_date: '',
  attachment_ids: [],          // already-saved attachments visible in edit mode
  new_attachment_ids: [],      // attachments uploaded during this session
});

export function SupervisorLogPanel({ employee, readOnly = false }) {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [dialogState, setDialogState] = useState({ open: false, mode: 'create', entryId: null });
  const [form, setForm] = useState(blankForm());
  const [exporting, setExporting] = useState(false);
  const fileInputRef = useRef(null);
  // The server decides who may write — see list_supervisor_log. Default to
  // false so nothing flashes before the first load resolves.
  const [perms, setPerms] = useState({ can_write: false, can_delete: false });

  const canCreate = !readOnly && perms.can_write;
  const canEdit = !readOnly && perms.can_write;
  const canDelete = perms.can_delete;  // spec: ONLY admin + HR

  useEffect(() => { if (employee?.id) load(); }, [employee?.id]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await supervisorLogAPI.list(employee.id);
      const d = res.data || {};
      setEntries(d.entries || []);
      setPerms({ can_write: !!d.can_write, can_delete: !!d.can_delete });
    } catch (e) {
      if (e.response?.status !== 403) toast.error('Failed to load supervisor log');
      setEntries([]);
      setPerms({ can_write: false, can_delete: false });
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setForm(blankForm());
    setDialogState({ open: true, mode: 'create', entryId: null });
  };

  const openEdit = (entry) => {
    setForm({
      entry_type: entry.entry_type,
      categories: entry.categories || [],
      incident_date: entry.incident_date,
      description: entry.description,
      corrective_action: entry.corrective_action || '',
      follow_up_required: !!entry.follow_up_required,
      follow_up_date: entry.follow_up_date || '',
      attachment_ids: entry.attachment_ids || [],
      new_attachment_ids: [],
      existing_attachments: entry.attachments || [],
    });
    setDialogState({ open: true, mode: 'edit', entryId: entry.id });
  };

  const closeDialog = () => setDialogState({ open: false, mode: 'create', entryId: null });

  const toggleCategory = (c) => {
    setForm(prev => ({
      ...prev,
      categories: prev.categories.includes(c)
        ? prev.categories.filter(x => x !== c)
        : [...prev.categories, c],
    }));
  };

  const handleFiles = async (files) => {
    if (!files || !files.length) return;
    const ALLOWED = /\.(pdf|docx|jpe?g|png)$/i;
    setBusy(true);
    const accepted = [];
    for (const file of files) {
      if (!ALLOWED.test(file.name)) {
        toast.error(`${file.name}: only PDF, DOCX, JPG, PNG are supported`);
        continue;
      }
      try {
        const fd = new FormData();
        fd.append('file', file);
        if (dialogState.entryId) fd.append('log_entry_id', dialogState.entryId);
        const res = await supervisorLogAPI.uploadAttachment(employee.id, fd);
        accepted.push(res.data);
      } catch (e) {
        toast.error(e.response?.data?.detail || `Failed to upload ${file.name}`);
      }
    }
    if (accepted.length) {
      setForm(prev => ({
        ...prev,
        new_attachment_ids: [...prev.new_attachment_ids, ...accepted.map(a => a.id)],
        existing_attachments: [...(prev.existing_attachments || []), ...accepted],
      }));
    }
    setBusy(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async () => {
    if (!form.description.trim()) { toast.error('Description is required'); return; }
    if (form.follow_up_required && !form.follow_up_date) { toast.error('Follow-up date is required'); return; }
    setBusy(true);
    try {
      if (dialogState.mode === 'create') {
        // All staged uploads in create mode were saved with log_entry_id=null; pass them as attachment_ids.
        await supervisorLogAPI.create(employee.id, {
          entry_type: form.entry_type,
          categories: form.categories,
          incident_date: form.incident_date,
          description: form.description.trim(),
          corrective_action: form.corrective_action.trim() || null,
          follow_up_required: form.follow_up_required,
          follow_up_date: form.follow_up_required ? form.follow_up_date : null,
          attachment_ids: form.new_attachment_ids,
        });
        toast.success('Log entry saved');
      } else {
        await supervisorLogAPI.update(dialogState.entryId, {
          entry_type: form.entry_type,
          categories: form.categories,
          incident_date: form.incident_date,
          description: form.description.trim(),
          corrective_action: form.corrective_action.trim() || null,
          follow_up_required: form.follow_up_required,
          follow_up_date: form.follow_up_required ? form.follow_up_date : null,
          new_attachment_ids: form.new_attachment_ids,
        });
        toast.success('Log entry updated');
      }
      closeDialog();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save entry');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (entry) => {
    if (!window.confirm('Delete this log entry permanently? Any attachments linked to it will also be removed.')) return;
    try {
      await supervisorLogAPI.delete(entry.id);
      toast.success('Entry deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not delete');
    }
  };

  const handleDeleteAttachment = async (att) => {
    if (!canDelete) return;
    if (!window.confirm(`Delete "${att.filename}"? This will remove it from the log entry and the Attachments tab.`)) return;
    try {
      await employeeAttachmentsAPI.delete(att.id);
      toast.success('Attachment deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const handleDownloadAttachment = async (att) => {
    try {
      const res = await employeeAttachmentsAPI.download(att.id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: att.content_type || 'application/octet-stream' }));
      const a = document.createElement('a');
      a.href = url; a.download = att.filename; a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed');
    }
  };

  const handleExport = async () => {
    if (readOnly) return;
    setExporting(true);
    try {
      const res = await supervisorLogAPI.exportPdf(employee.id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `SupervisorLog_${employee.last_name || 'Employee'}_${todayStr()}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'PDF export failed');
    } finally {
      setExporting(false);
    }
  };

  const formatTs = (ts) => {
    if (!ts) return '';
    try { return new Date(ts).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
    catch { return ts.slice(0, 10); }
  };

  return (
    <Card data-testid="supervisor-log-panel">
      <CardHeader className="flex flex-row items-start justify-between gap-3">
        <div>
          <CardTitle>Supervisor Log {readOnly && <Badge variant="outline" className="ml-2 text-xs">read-only</Badge>}</CardTitle>
          <CardDescription>Internal record of deficiencies, positive observations, and general log entries. Not visible to the employee.</CardDescription>
        </div>
        <div className="flex gap-2 flex-wrap">
          {!readOnly && entries.length > 0 && (
            <Button variant="outline" onClick={handleExport} disabled={exporting} data-testid="export-supervisor-log-pdf-btn">
              <Printer className="w-4 h-4 mr-2" /> {exporting ? 'Exporting…' : 'Export to PDF'}
            </Button>
          )}
          {canCreate && (
            <Button onClick={openCreate} data-testid="new-log-entry-btn">
              <Plus className="w-4 h-4 mr-2" /> New Entry
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="h-24 skeleton-pulse rounded" />
        ) : entries.length === 0 ? (
          <div className="text-center py-10 text-slate-500">
            <ClipboardIcon className="w-10 h-10 mx-auto mb-2 text-slate-300" />
            <p>No log entries yet</p>
          </div>
        ) : (
          <ul className="space-y-3">
            {entries.map(e => {
              const type = TYPE_BY_VALUE[e.entry_type] || TYPE_BY_VALUE.log_entry;
              const TypeIcon = type.Icon;
              const atts = e.attachments || [];
              return (
                <li
                  key={e.id}
                  className={cn('border-l-4 rounded-md p-3 bg-surface shadow-sm', type.border)}
                  data-testid={`log-entry-${e.id}`}
                >
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={cn(type.badge, 'uppercase')}>
                        <TypeIcon className="w-3 h-3 mr-1" />
                        {type.label}
                      </Badge>
                      {(e.categories || []).map(c => (
                        <Badge key={c} variant="outline" className="text-xs">{CATEGORY_LABEL[c] || c}</Badge>
                      ))}
                      {e.follow_up_required && (
                        <Badge className="bg-amber-100 text-amber-800 text-xs" data-testid={`follow-up-flag-${e.id}`}>
                          <Bell className="w-3 h-3 mr-1" />
                          Follow-up by {e.follow_up_date}
                        </Badge>
                      )}
                      {atts.length > 0 && (
                        <Badge variant="outline" className="text-xs" title={`${atts.length} attachment(s)`} data-testid={`attachment-flag-${e.id}`}>
                          <Paperclip className="w-3 h-3 mr-1" /> {atts.length}
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 flex items-center gap-2 flex-wrap">
                      <span>{e.incident_date} · {e.supervisor_name}</span>
                      {canEdit && !readOnly && (
                        <button
                          className="text-slate-600 hover:text-slate-900 hover:underline"
                          onClick={() => openEdit(e)}
                          data-testid={`edit-log-${e.id}`}
                        ><Pencil className="w-3 h-3 inline" /> Edit</button>
                      )}
                      {canDelete && !readOnly && (
                        <button
                          className="text-red-600 hover:underline"
                          onClick={() => handleDelete(e)}
                          data-testid={`delete-log-${e.id}`}
                        ><Trash2 className="w-3 h-3 inline" /></button>
                      )}
                    </div>
                  </div>
                  <p className="text-sm mt-2 whitespace-pre-wrap">{e.description}</p>
                  {e.corrective_action && (
                    <p className="text-xs text-slate-600 mt-2"><b>Corrective action:</b> {e.corrective_action}</p>
                  )}
                  {atts.length > 0 && (
                    <div className="mt-3 border-t pt-2 space-y-1" data-testid={`attachment-list-${e.id}`}>
                      {atts.map(att => (
                        <div key={att.id} className="flex items-center gap-2 text-xs">
                          <FileText className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                          <button
                            className="hover:underline text-slate-700 truncate"
                            onClick={() => handleDownloadAttachment(att)}
                            data-testid={`download-att-${att.id}`}
                            title={att.filename}
                          >{att.filename}</button>
                          <Button variant="ghost" size="sm" className="h-6 px-2" onClick={() => handleDownloadAttachment(att)}>
                            <Download className="w-3.5 h-3.5" />
                          </Button>
                          {canDelete && !readOnly && (
                            <Button variant="ghost" size="sm" className="h-6 px-2 text-red-600" onClick={() => handleDeleteAttachment(att)} data-testid={`delete-att-${att.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {e.last_edited_at && (
                    <p className="text-[11px] text-slate-400 italic mt-2" data-testid={`last-edited-${e.id}`}>
                      Last edited {formatTs(e.last_edited_at)} by {e.last_edited_by_name || '—'}
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>

      {/* New / Edit Entry Dialog */}
      <Dialog open={dialogState.open} onOpenChange={(o) => { if (!o) closeDialog(); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{dialogState.mode === 'edit' ? 'Edit Log Entry' : 'New Supervisor Log Entry'}</DialogTitle>
            <DialogDescription>
              For <b>{employee?.first_name} {employee?.last_name}</b> · entered by <b>{user?.first_name} {user?.last_name}</b>
              {dialogState.mode === 'edit' && <span className="ml-2 text-amber-700 text-xs">Employee Name and Supervisor Name are not editable.</span>}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Entry Type</Label>
                <Select value={form.entry_type} onValueChange={(v) => setForm(p => ({ ...p, entry_type: v }))}>
                  <SelectTrigger data-testid="entry-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ENTRY_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Date of Incident</Label>
                <Input type="date" value={form.incident_date} onChange={(ev) => setForm(p => ({ ...p, incident_date: ev.target.value }))} data-testid="incident-date" />
              </div>
            </div>

            <div>
              <Label>Categories (select all that apply)</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {CATEGORIES.map(c => (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => toggleCategory(c.value)}
                    className={cn(
                      'px-3 py-1 rounded-md text-xs border transition-colors',
                      form.categories.includes(c.value)
                        ? 'bg-brand text-brand-contrast border-brand'
                        : 'bg-surface text-slate-700 border-slate-300 hover:bg-slate-100',
                    )}
                    data-testid={`category-toggle-${c.value}`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label>Description / Narrative *</Label>
              <Textarea rows={4} value={form.description} onChange={(ev) => setForm(p => ({ ...p, description: ev.target.value }))} data-testid="entry-description" />
            </div>

            <div>
              <Label>Corrective Action Taken (optional)</Label>
              <Textarea rows={2} value={form.corrective_action} onChange={(ev) => setForm(p => ({ ...p, corrective_action: ev.target.value }))} data-testid="entry-corrective" />
            </div>

            <div className="flex items-center justify-between pt-1">
              <Label htmlFor="follow-up-switch" className="cursor-pointer">Follow-up required?</Label>
              <Switch id="follow-up-switch" checked={form.follow_up_required} onCheckedChange={(v) => setForm(p => ({ ...p, follow_up_required: v }))} data-testid="follow-up-switch" />
            </div>

            {form.follow_up_required && (
              <div>
                <Label>Follow-up Date *</Label>
                <Input type="date" value={form.follow_up_date} onChange={(ev) => setForm(p => ({ ...p, follow_up_date: ev.target.value }))} data-testid="follow-up-date" />
              </div>
            )}

            <div>
              <Label>Attachments (PDF, DOCX, JPG, PNG)</Label>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,.docx,.jpg,.jpeg,.png"
                className="hidden"
                onChange={(ev) => handleFiles(Array.from(ev.target.files || []))}
                data-testid="entry-file-input"
              />
              <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={busy} data-testid="entry-upload-btn">
                <Upload className="w-4 h-4 mr-2" /> Attach files
              </Button>
              {(form.existing_attachments || []).length > 0 && (
                <ul className="mt-2 space-y-1">
                  {(form.existing_attachments || []).map(att => (
                    <li key={att.id} className="text-xs flex items-center gap-2 text-slate-700">
                      <FileText className="w-3.5 h-3.5 text-slate-400" />
                      <span className="truncate" title={att.filename}>{att.filename}</span>
                      {form.new_attachment_ids.includes(att.id) && <Badge variant="outline" className="text-[10px]">New</Badge>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={busy} data-testid="save-log-entry-btn">
              {busy ? 'Saving…' : (dialogState.mode === 'edit' ? 'Save Changes' : 'Save Entry')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
